// @flow
import * as React from 'react';
import Icon from 'sail/Icon';
import {Heading, List, ListItem, InlineCode} from 'docs/components';
import {renderMarkdocAsReact} from 'docs/markdoc';
import './ReportingCategoryList.scss';
import {defineMessages, FormattedMessage} from 'react-intl';
import type {
  ReportsContext,
  ReportsContextReportingCategory as ReportingCategory,
  ReportsContextReportingCategoryGroup as ReportingCategoryGroup,
} from 'docs/types';

const messages = defineMessages({
  btTypesHeader: {
    id: 'docsApp.components.ReportingCategoryList.btTypesHeader',
    defaultMessage: 'Balance transaction type(s): ',
    description:
      'Subheader text which is followed by a list of balance transaction types for this reporting category',
  },
  reportSectionHeader: {
    id: 'docsApp.components.ReportingCategoryList.reportSectionHeader',
    defaultMessage: 'Section(s) in the monthly report: ',
    description:
      'Subheader text which is followed by a list of sections in the monthly report for this reporting category',
  },
});

type ReportingCategoriesMap = $PropertyType<
  ReportsContext,
  'reporting_categories_map',
>;

type ReportingCategoryGroupList = $PropertyType<
  ReportsContext,
  'reporting_category_groups',
>;

type ReportingCategoryTOCProps = {|
  reportingCategoryGroups: ReportingCategoryGroupList,
|};

type ReportingCategorySectionProps = {|
  reportingCategoryGroupElements: Array<ReportingCategory>,
  reportingCategoryGroup: ReportingCategoryGroup,
|};

type ReportingCategoryItemProps = {|
  reportingCategory: ReportingCategory,
|};

type ReportingCategoryListProps = {|
  reportingCategoryGroups: ReportingCategoryGroupList,
  reportingCategoryMap: ReportingCategoriesMap,
|};

type HumanReadableCodeListProps = {|
  items: Array<string>,
|};

const ReportingCategoryTOC = ({
  reportingCategoryGroups,
}: ReportingCategoryTOCProps) => {
  const groupElements = reportingCategoryGroups.map((group) => (
    <ListItem key={group.name}>
      <a href={`#${group.name}`}>{group.title}</a>
    </ListItem>
  ));
  return <List>{groupElements}</List>;
};

// Format a list of strings into a string list that is human readable.
// E.g. ['one', 'two', 'three] => 'one, two, or three'
const HumanReadableCodeList = ({items}: HumanReadableCodeListProps) => {
  if (items.length >= 2) {
    const formattedCommaItems = items.slice(0, -1).map((item, idx) => {
      return (
        <>
          <InlineCode>{item}</InlineCode>
          {items.length === 2 ? '' : ', '}
        </>
      );
    });
    return (
      <>
        {formattedCommaItems}
        {'or '}
        <InlineCode>{items[items.length - 1]}</InlineCode>
      </>
    );
  } else {
    // Return a single item by itself
    return <InlineCode>{items[0]}</InlineCode>;
  }
};

const ReportingCategoryItem = ({
  reportingCategory,
}: ReportingCategoryItemProps) => {
  return (
    <>
      <dt>
        <Icon className="inline" icon="checkCircle" color="green" />
        <Heading
          className="inline"
          level={3}
          id={reportingCategory.id.replace(/_/g, '-')}
          anchored
        >
          <InlineCode>{reportingCategory.id}</InlineCode>
        </Heading>
      </dt>
      <dd>
        <p>{renderMarkdocAsReact(reportingCategory.description)}</p>
        <List>
          <ListItem>
            <FormattedMessage {...messages.btTypesHeader} />
            <HumanReadableCodeList
              items={reportingCategory.balance_transaction_types}
            />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.reportSectionHeader} />
            <i>{`${reportingCategory.monthly_report_sections}`}</i>
          </ListItem>
        </List>
      </dd>
    </>
  );
};

const ReportingCategorySection = ({
  reportingCategoryGroupElements,
  reportingCategoryGroup,
}: ReportingCategorySectionProps) => {
  const categoryItems = reportingCategoryGroupElements.map((category) => (
    <ReportingCategoryItem reportingCategory={category} />
  ));
  return (
    <>
      <h3 id={`#${reportingCategoryGroup.name}`}>
        {reportingCategoryGroup.title}
      </h3>
      <p>{renderMarkdocAsReact(reportingCategoryGroup.description)}</p>
      <dl className="ReportingCategoryList">{categoryItems}</dl>
    </>
  );
};

export const ReportingCategoryList = ({
  reportingCategoryGroups,
  reportingCategoryMap,
}: ReportingCategoryListProps) => {
  const categorySections = reportingCategoryGroups.map((group) => (
    <ReportingCategorySection
      reportingCategoryGroup={group}
      reportingCategoryGroupElements={reportingCategoryMap[group.name]}
    />
  ));
  return (
    <>
      <ReportingCategoryTOC reportingCategoryGroups={reportingCategoryGroups} />
      {categorySections}
    </>
  );
};
